import { useState } from "react";
import { ReservationFormData } from "../types";
import type { Locale } from "@/config/i18n";
import { getAttribution } from "@/lib/attribution-client";

/**
 * Soumission de la réservation vers `/api/reservation`.
 *
 * Le corps POST reprend `...formData`, `...customerInfo` (dont `countryCode`)
 * et `locale` — exactement comme l'implémentation d'origine de
 * `useReservationForm`. Aucun changement de payload ni de comportement.
 */
export const useReservationSubmit = (
  formData: ReservationFormData,
  locale: Locale,
) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const submitReservation = async (customerInfo: {
    firstName: string;
    lastName: string;
    phone: string;
    email: string;
    countryCode?: string;
    company?: string;
  }) => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const response = await fetch("/api/reservation", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          attribution: getAttribution(),
          ...formData,
          ...customerInfo,
          locale,
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          typeof data?.error === "string"
            ? data.error
            : "Une erreur est survenue",
        );
      }

      return { success: true };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Une erreur est survenue";
      setSubmitError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    isSubmitting,
    setIsSubmitting,
    submitError,
    setSubmitError,
    submitReservation,
  };
};
